// The rules an automatic course import obeys — set in the Console, not in code.
//
// ── Omar, 2026-09-18 (verbatim) ─────────────────────────────────────────────
//
//   "I want to manage the rules for automatic course generation directly from
//    the Console, so I can change how future imports work without code changes
//    or deployment. … The saved rules must govern both generation and
//    validation. Each import should use one consistent set of rules from its
//    start. Settings changes apply to subsequent imports and must not modify
//    existing courses or author edits."
//
// ★ WHY THIS LIVES IN `@dynamo/schema` AND NOT IN EITHER APP.
//
// Three places need the same seven numbers: the Console EDITS them, the gateway
// INJECTS them into the outline prompt, and the gateway CHECKS the result
// against them. Those are three faces of one rule and they were about to be
// three lists that agreed only while somebody remembered to edit all three
// (`feedback_one_rule_one_place`). The same argument that put
// `ai-cost-categories.ts` here puts this here.
//
// ── ★ THE INVARIANT THAT IS NOT A SETTING, AND CANNOT BECOME ONE ────────────
//
// Omar, same message: *"Preserve source content and meaning. Do not invent,
// duplicate or pad content to satisfy minimum screen counts, character counts
// or video quotas."*
//
// So every number below is a CEILING ON AMBITION, never a quota to fill. When a
// document cannot support a rule, the rule loses and the shortfall is REPORTED —
// there is no code path that manufactures a screen, a tab or a video to make a
// number come out right, and `COURSE_IMPORT_INVARIANTS` states that on the
// Console screen so it is visible rather than remembered. Same structural move
// as `ai_image_profile` having no column a picture's dimensions could go in:
// the guarantee is a property of the shape, not a rule someone has to keep.
//
// ⚠️ AND A SETTING IS NOT A MIGRATION. Changing a rule changes what the NEXT
// import produces. It never rewrites a course that already exists and never
// touches an author's edits — the rules are read once, at the start of a run,
// and that snapshot is what the whole run uses (`CourseImportRulesSnapshot`).
import { z } from "zod";
/**
 * The sentinel for "this band has no upper limit" (the brief's `261+` row).
 *
 * ★ WHY NOT `Infinity`. These values are stored as JSON and travel over HTTP.
 * `JSON.stringify({ maxWords: Infinity })` produces `{"maxWords":null}` — the
 * ceiling would not round-trip, it would come back as an absence, and the top
 * band would silently stop validating. `NaN` does the same. So the top band
 * carries a real integer that is far above any reachable word count (the largest
 * screen ever measured is 2,240 words) and is recognised by identity, not by
 * being large.
 */
export const WORD_BAND_NO_UPPER_LIMIT = Number.MAX_SAFE_INTEGER;
/**
 * ★ THE TWO LAYOUTS THAT HAVE NO WORD BAND, AND WHY — carried as data so the
 * reason travels with the table instead of living in a comment somebody has to
 * go and find. A test asserts both are absent from `wordBands` BY NAME.
 *
 * Both omissions are deliberate. Neither is forgotten.
 */
export const COURSE_IMPORT_LAYOUTS_WITHOUT_WORD_BAND = Object.freeze({
    title: "Rule 4 removes `title` from selection entirely, so a band for it could only " +
        "produce a contradiction at runtime — a lookup returning a layout another " +
        "rule forbids, resolved by whichever check happens to run last.",
    quiz_images: "Rule 7 decides when `quiz_images` is used — the document contains questions " +
        "— which has nothing to do with length. Its measured range (130–810 words, " +
        "and up to 2,240 in one course that compresses a whole game into it) is not " +
        "representative of a quiz and must never be an INPUT to selection.",
});
/**
 * ★ THE FACTORY VALUES — Omar's seven, exactly as he wrote them.
 *
 * Frozen, and a constant in code rather than a seeded row, for the same reason
 * `FACTORY_IMAGE_PROFILE` is: "restore the default" must be a DELETE of an
 * override back to something that cannot itself be deleted or drift.
 */
export const FACTORY_COURSE_IMPORT_RULES = Object.freeze({
    minScreensPerModule: 3,
    alternateImagePlacement: true,
    openingLayouts: Object.freeze([
        "fullscreen_text_and_image",
        "fullscreen_video",
        "small_video",
    ]),
    verticalTabsMinBodyChars: 200,
    coverageCheck: true,
    // 1 = "Do not place two screens of the same layout type consecutively".
    maxConsecutiveSameLayout: 1,
    minVideoScreensPerCourse: 2,
    // ★ Omar's table of 2026-09-18, transcribed exactly. `fullscreen_text_and_image`
    // has no subtitle field at all, which is why its `subtitle` is false rather
    // than an oversight — the permission would have nowhere to apply.
    composableFields: Object.freeze({
        fullscreen_text_and_image: Object.freeze({ title: true, subtitle: false, labels: false }),
        title: Object.freeze({ title: true, subtitle: true, labels: false }),
        text_and_image: Object.freeze({ title: true, subtitle: true, labels: false }),
        two_columns_text: Object.freeze({ title: true, subtitle: true, labels: false }),
        fullscreen_video: Object.freeze({ title: true, subtitle: true, labels: false }),
        small_video: Object.freeze({ title: true, subtitle: true, labels: false }),
        vertical_tabs: Object.freeze({ title: true, subtitle: true, labels: true }),
        horizontal_tabs: Object.freeze({ title: true, subtitle: true, labels: true }),
        // ⚠️ `labels: false`, corrected 2026-09-23 — THE 26th, and the same defect
        // the `icons_discover` note below describes, missed by the same audit. A
        // sequence dot was `{ kind, mobileText, imageUrl, imageAlt, text }` and
        // `.strict()`: there was no name field of any kind, so this permission asked
        // the model for words with nowhere to put them. (Since 2026-10-26, Plan 37 ③,
        // a step also holds `tabTitle`: its phone label per language. That is a
        // label for a phone, not a name the import writes; `labels` stays false.)
        //
        // ★ FOUND ON A REAL COURSE, not by reading. On `8b2ceb9d` (run `0cd64ded`)
        //   a three-item source list became a two-dot sequence and ALL THREE item
        //   labels went: "Due diligence in the supply chain:" survived only because
        //   it happened to be lifted into the screen title, and "Onboarding
        //   process:" and "Customer contracts:" are nowhere in the saved course.
        //   They are the ONLY wording of that 19,725-character document that is
        //   missing from it — too little to move any percentage, which is why no
        //   coverage target would ever have found them.
        //
        // `composable-labels.test.ts` now derives this from the schemas and fails
        // if a layout is granted a label it has no field for, so there is no third
        // time (`feedback_derive_localizedstring_set_from_schema`).
        sequence: Object.freeze({ title: true, subtitle: true, labels: false }),
        // ⚠️ `labels: false`, corrected 2026-09-21 — and it is NOT a narrowing of
        // Omar's table, because the permission had nowhere to land. An icon is
        // `{ imageUrl, imageAlt, text }` and `.strict()`: there is no label field,
        // which is why the Console already draws a dash in this cell and why the
        // request slot was removed from the writer on 2026-09-19. Granting it asked
        // the model for words that were then thrown away on every import. The
        // factory granted 25 permissions where 24 fields exist; this is the 25th.
        icons_discover: Object.freeze({ title: true, subtitle: true, labels: false }),
        quiz_images: Object.freeze({ title: true, subtitle: true, labels: false }),
    }),
    sourceQuestionsUseQuizLayout: true,
    varyLayoutsAcrossModules: true,
    // Empty = the generator's own closed set, whatever that is today. Naming the
    // eleven here as well would be a second copy of a list that already exists in
    // the gateway (`feedback_one_rule_one_place`).
    allowedLayouts: Object.freeze([]),
    // ── The 2026-09-21 layout rules ───────────────────────────────────────────
    //
    // ★ EVERY VALUE BELOW IS TRANSCRIBED FROM `Course_import_layout_rules_design.md`
    // §3.2 (Rule 1) AND THE BRIEF'S RULE 2 / 3 / 4 / 6 TABLES. §3.2 is the RATIFIED
    // version and wins over the brief wherever they differ — the brief's own table
    // still carries the `title` row and the `quiz_images` row that D7 removed from
    // selection, and a transcription from the wrong copy would put both back.
    // Rule 1 — §3.2's "p25–p75 — selects" column, in the table's own order.
    // ⚠️ `title` and `quiz_images` have NO ROW, deliberately. The reason for each
    // is in `COURSE_IMPORT_LAYOUTS_WITHOUT_WORD_BAND`; a test asserts both are
    // absent by name so neither can be "restored" as a tidy-up later.
    // ⚠️ `hidden_items`' band rests on **11** screens against 120 for
    // `text_and_image` — the thinnest evidence in the table, and D2 has just made
    // it generatable, so it will be reached for often. Any finding about it carries
    // that `n` rather than the same confidence as its neighbours.
    wordBands: Object.freeze({
        fullscreen_video: Object.freeze({ p25: 9, p75: 26 }),
        small_video: Object.freeze({ p25: 14, p75: 30 }),
        fullscreen_text_and_image: Object.freeze({ p25: 28, p75: 53 }),
        text_and_image: Object.freeze({ p25: 41, p75: 90 }),
        two_columns_text: Object.freeze({ p25: 84, p75: 129 }),
        icons_discover: Object.freeze({ p25: 109, p75: 217 }),
        horizontal_tabs: Object.freeze({ p25: 109, p75: 191 }),
        hidden_items: Object.freeze({ p25: 110, p75: 164 }),
        sequence: Object.freeze({ p25: 117, p75: 199 }),
        vertical_tabs: Object.freeze({ p25: 145, p75: 312 }),
    }),
    // Rule 1 — the three limits clean across all 508 measured screens.
    // ⚠️ Descriptions of six courses, NOT limits the Player enforces. See the
    // field's own doc comment; this is a policy choice, not a discovered ceiling.
    wordBandHardLimits: Object.freeze([
        Object.freeze({ layout: "fullscreen_video", direction: "never_above", words: 34 }),
        Object.freeze({ layout: "fullscreen_text_and_image", direction: "never_above", words: 89 }),
        Object.freeze({ layout: "vertical_tabs", direction: "never_below", words: 63 }),
    ]),
    // Rule 2 — the brief's bands 0–20 · 21–40 · 41–70 · 71–110 · 111–170 ·
    // 171–260 · 261+, each row in the confirmed priority order (weight 1 first).
    // The lower edge of a row is the previous row's `maxWords` + 1; only the upper
    // edge is stored, so two adjacent rows cannot disagree about where they meet.
    bandWeights: Object.freeze([
        Object.freeze({
            maxWords: 20,
            layouts: Object.freeze(["small_video", "fullscreen_video", "fullscreen_text_and_image"]),
        }),
        Object.freeze({
            maxWords: 40,
            layouts: Object.freeze(["fullscreen_text_and_image", "text_and_image", "small_video"]),
        }),
        Object.freeze({
            maxWords: 70,
            layouts: Object.freeze(["text_and_image", "fullscreen_text_and_image"]),
        }),
        Object.freeze({
            maxWords: 110,
            layouts: Object.freeze([
                "text_and_image",
                "sequence",
                "icons_discover",
                "two_columns_text",
                "hidden_items",
            ]),
        }),
        Object.freeze({
            maxWords: 170,
            layouts: Object.freeze([
                "sequence",
                "vertical_tabs",
                "horizontal_tabs",
                "two_columns_text",
                "hidden_items",
                "icons_discover",
            ]),
        }),
        Object.freeze({
            maxWords: 260,
            layouts: Object.freeze(["vertical_tabs", "icons_discover"]),
        }),
        // The brief's `261+` row. See `WORD_BAND_NO_UPPER_LIMIT` for why this is not
        // `Infinity` — `JSON.stringify` turns `Infinity` into `null`, so the ceiling
        // would come back from storage as an absence.
        Object.freeze({
            maxWords: WORD_BAND_NO_UPPER_LIMIT,
            layouts: Object.freeze(["sequence", "vertical_tabs"]),
        }),
    ]),
    // Rule 3 — the three approved openings a / b / c, in the brief's order.
    // ⚠️ This supersedes NOTHING: `openingLayouts` above keeps its own meaning and
    // its own value. See the field's doc comment for how the two relate.
    openingSequences: Object.freeze([
        Object.freeze(["fullscreen_text_and_image", "sequence", "text_and_image"]),
        Object.freeze(["fullscreen_text_and_image", "small_video", "text_and_image"]),
        Object.freeze(["fullscreen_text_and_image", "horizontal_tabs", "text_and_image"]),
    ]),
    // Rule 4 — the ONE exception to the global no-repeat rule. Every layout not
    // named here falls back to `maxConsecutiveSameLayout` above, which is 1.
    maxConsecutiveByLayout: Object.freeze({ text_and_image: 3 }),
    // Rule 6 — 7 dots, counting a closing question and its feedback.
    maxSequenceDots: 7,
    // D6 — the permitted counts, and ONLY the counts. The 2/3 alternation is
    // instrumented, never encoded; there is deliberately nowhere here to put it.
    sequenceQuestionAnswerCounts: Object.freeze([2, 3]),
    // Rule 6 — the closing question + feedback pair is ON by default, which is
    // what the brief asks for. It is a switch because it changes what an import
    // CONTAINS rather than what is recorded about it; see the field's comment.
    composeSequenceQuestions: true,
    // D1 — `vertical_tabs` and `sequence` minimum 2. Today's schema permits 1 for
    // both, so this is a change to the rule, not a restatement of it.
    minItemsByLayout: Object.freeze({ vertical_tabs: 2, sequence: 2 }),
    // Rule 2-bis — 110 characters per column. Measured, not chosen: the thinnest
    // column in the six shipped courses is 118 characters and none is empty, so
    // this floor cannot flag anything a person wrote, and it does flag the 12-
    // and 15-character columns the 2026-09-22 import produced.
    twoColumnsMinColumnChars: 110,
    // Rule 9. Omar's number, transcribed: 16 characters, spaces included.
    tabTitleMaxChars: 16,
    // Rule 10. Omar's decision of 2026-09-23: both video layouts count as one,
    // at most one video per module.
    balanceLayoutsWithinModule: true,
    maxVideoScreensPerModule: 1,
    // ⚠️ OFF — see the field's comment. It collides with "the model's own choice
    // is left alone", and that is Omar's call.
    applyBandOrderToModelChoices: true,
});
/**
 * Bounds, in ONE place, so the Console's number inputs and the gateway's
 * validator cannot disagree about what is acceptable.
 *
 * ⚠️ The upper bounds are not taste. `minScreensPerModule` above 9 contradicts
 * the outline prompt's own "a module with twenty is a chapter"; a tab-body
 * threshold above `vertical_tabs`' 600-character budget would make the layout
 * unreachable by construction rather than by content.
 */
export const COURSE_IMPORT_RULE_BOUNDS = Object.freeze({
    minScreensPerModule: Object.freeze({ min: 1, max: 9 }),
    verticalTabsMinBodyChars: Object.freeze({ min: 0, max: 600 }),
    maxConsecutiveSameLayout: Object.freeze({ min: 1, max: 5 }),
    minVideoScreensPerCourse: Object.freeze({ min: 0, max: 20 }),
    // Rule 6. The floor is 2 because a one-dot `sequence` is not a sequence; the
    // ceiling is 8 because that is what the generator permits today, so a person
    // cannot type a number that would silently widen the rule past the code.
    maxSequenceDots: Object.freeze({ min: 2, max: 8 }),
    twoColumnsMinColumnChars: Object.freeze({ min: 0, max: 600 }),
    // 0 turns the rule off; 60 is far past any tab button and still bounds a typo.
    tabTitleMaxChars: Object.freeze({ min: 0, max: 60 }),
    maxVideoScreensPerModule: Object.freeze({ min: 0, max: 5 }),
});
/**
 * Bounds on the numbers INSIDE a rule — a band edge, a per-layout override — as
 * opposed to the numbers a rule IS.
 *
 * ★ WHY THIS IS A SECOND CONSTANT AND NOT MORE ROWS IN THE ONE ABOVE.
 * `COURSE_IMPORT_RULE_BOUNDS` is keyed by RULE KEY and the Console walks it to
 * draw one number input per numeric rule, asserting on screen that every one of
 * them states its permitted range. A key in there that is not a rule would make
 * that screen promise a control that cannot exist, and the invariant "every
 * numeric rule shows its range" would quietly become "every entry that happens
 * to have a control". Two constants keep both statements true.
 */
export const COURSE_IMPORT_NESTED_BOUNDS = Object.freeze({
    /** Any whole-screen word figure: a band edge, a hard limit, a ranking row's top. */
    wordCount: Object.freeze({ min: 0, max: 5000 }),
    /** A per-layout consecutive-repeat override — the same ceiling as the global rule. */
    layoutRepeat: Object.freeze({ min: 1, max: 5 }),
    /** A per-layout minimum item count: tabs, dots, icons, hotspots. */
    layoutMinItems: Object.freeze({ min: 1, max: 20 }),
    /** How many answers a COMPOSED sequence question may carry. */
    answerCount: Object.freeze({ min: 2, max: 6 }),
});
/** Any whole-screen word figure inside a rule — a band edge, a hard limit. */
const nestedWordCount = z
    .number()
    .int()
    .min(COURSE_IMPORT_NESTED_BOUNDS.wordCount.min)
    .max(COURSE_IMPORT_NESTED_BOUNDS.wordCount.max);
/**
 * A ranking row's upper edge: a word count, OR the "no ceiling" sentinel.
 *
 * ⚠️ The sentinel has to be admitted by IDENTITY rather than by raising the
 * ceiling, or the bound stops being a bound: `.max(WORD_BAND_NO_UPPER_LIMIT)`
 * would accept 900,000 as a perfectly ordinary band edge.
 */
const bandUpperEdge = z
    .number()
    .int()
    .refine((v) => v === WORD_BAND_NO_UPPER_LIMIT || (v >= 1 && v <= COURSE_IMPORT_NESTED_BOUNDS.wordCount.max), {
    message: `A band's upper edge is a word count from 1 to ${COURSE_IMPORT_NESTED_BOUNDS.wordCount.max}, ` +
        `or ${WORD_BAND_NO_UPPER_LIMIT} for "no upper limit".`,
});
/**
 * What the Console stores. **Every field optional; absent means "use the
 * factory value for this rule"** — the per-field null semantics of
 * `StoredImageProfileFields`, which is what makes "restore defaults" a removal
 * rather than a copy of today's default frozen into a row.
 */
export const CourseImportRuleOverridesSchema = z
    .object({
    minScreensPerModule: z
        .number()
        .int()
        .min(COURSE_IMPORT_RULE_BOUNDS.minScreensPerModule.min)
        .max(COURSE_IMPORT_RULE_BOUNDS.minScreensPerModule.max)
        .optional(),
    alternateImagePlacement: z.boolean().optional(),
    // Non-empty ids only; the ROUTE checks them against the generator's closed
    // set, because that set lives in the gateway and this package must not hold
    // a copy of it.
    openingLayouts: z.array(z.string().min(1)).max(32).optional(),
    verticalTabsMinBodyChars: z
        .number()
        .int()
        .min(COURSE_IMPORT_RULE_BOUNDS.verticalTabsMinBodyChars.min)
        .max(COURSE_IMPORT_RULE_BOUNDS.verticalTabsMinBodyChars.max)
        .optional(),
    twoColumnsMinColumnChars: z
        .number()
        .int()
        .min(COURSE_IMPORT_RULE_BOUNDS.twoColumnsMinColumnChars.min)
        .max(COURSE_IMPORT_RULE_BOUNDS.twoColumnsMinColumnChars.max)
        .optional(),
    tabTitleMaxChars: z
        .number()
        .int()
        .min(COURSE_IMPORT_RULE_BOUNDS.tabTitleMaxChars.min)
        .max(COURSE_IMPORT_RULE_BOUNDS.tabTitleMaxChars.max)
        .optional(),
    balanceLayoutsWithinModule: z.boolean().optional(),
    applyBandOrderToModelChoices: z.boolean().optional(),
    maxVideoScreensPerModule: z
        .number()
        .int()
        .min(COURSE_IMPORT_RULE_BOUNDS.maxVideoScreensPerModule.min)
        .max(COURSE_IMPORT_RULE_BOUNDS.maxVideoScreensPerModule.max)
        .optional(),
    coverageCheck: z.boolean().optional(),
    maxConsecutiveSameLayout: z
        .number()
        .int()
        .min(COURSE_IMPORT_RULE_BOUNDS.maxConsecutiveSameLayout.min)
        .max(COURSE_IMPORT_RULE_BOUNDS.maxConsecutiveSameLayout.max)
        .optional(),
    minVideoScreensPerCourse: z
        .number()
        .int()
        .min(COURSE_IMPORT_RULE_BOUNDS.minVideoScreensPerCourse.min)
        .max(COURSE_IMPORT_RULE_BOUNDS.minVideoScreensPerCourse.max)
        .optional(),
    // The layout ids are checked against the generator's closed set by the
    // ROUTE, for the same reason `openingLayouts` is.
    composableFields: z
        .record(z.string().min(1), z
        .object({
        title: z.boolean(),
        subtitle: z.boolean(),
        labels: z.boolean(),
    })
        .strict())
        .optional(),
    sourceQuestionsUseQuizLayout: z.boolean().optional(),
    varyLayoutsAcrossModules: z.boolean().optional(),
    allowedLayouts: z.array(z.string().min(1)).max(32).optional(),
    // ── The 2026-09-21 layout rules ────────────────────────────────────────
    //
    // ⚠️ Every nested object is `.strict()` too. A `.strict()` at the top level
    // only refuses an unknown RULE; without it on each row, `{ p25, p75, p90 }`
    // would be accepted and `p90` silently dropped — the shape of a silent key
    // strip (`feedback_silent_key_strip_hides_data_loss`).
    //
    // Layout ids are NOT checked against the generator's closed set here, for
    // the same reason `openingLayouts` is not: that set lives in the gateway and
    // this package must not hold a second copy of it.
    wordBands: z
        .record(z.string().min(1), z
        .object({ p25: nestedWordCount, p75: nestedWordCount })
        .strict()
        .refine((b) => b.p25 <= b.p75, {
        message: "A band's lower figure cannot be above its upper one.",
    }))
        .optional(),
    wordBandHardLimits: z
        .array(z
        .object({
        layout: z.string().min(1),
        // Stated, never inferred. See `LayoutWordLimit`.
        direction: z.enum(["never_above", "never_below"]),
        words: nestedWordCount,
    })
        .strict())
        .max(32)
        .optional(),
    bandWeights: z
        .array(z
        .object({
        maxWords: bandUpperEdge,
        layouts: z.array(z.string().min(1)).min(1).max(32),
    })
        .strict())
        .max(16)
        .optional()
        // ★ ASCENDING AND NON-OVERLAPPING IS PART OF THE SHAPE, not a convention
        // the reader is trusted with. Only the upper edge of each row is stored,
        // so a row out of order does not produce an overlap — it produces a band
        // with a NEGATIVE width that silently matches nothing, and a lookup that
        // matches nothing looks exactly like a lookup that found no candidate.
        .superRefine((bands, ctx) => {
        if (!bands)
            return;
        for (let i = 1; i < bands.length; i += 1) {
            if (bands[i].maxWords <= bands[i - 1].maxWords) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [i, "maxWords"],
                    message: "Each band must end above the one before it — the rows are read " +
                        "in order and a row's lower edge is the previous row's upper edge + 1.",
                });
            }
        }
    }),
    openingSequences: z
        .array(z.array(z.string().min(1)).min(1).max(8))
        .max(16)
        .optional(),
    maxConsecutiveByLayout: z
        .record(z.string().min(1), z
        .number()
        .int()
        .min(COURSE_IMPORT_NESTED_BOUNDS.layoutRepeat.min)
        .max(COURSE_IMPORT_NESTED_BOUNDS.layoutRepeat.max))
        .optional(),
    maxSequenceDots: z
        .number()
        .int()
        .min(COURSE_IMPORT_RULE_BOUNDS.maxSequenceDots.min)
        .max(COURSE_IMPORT_RULE_BOUNDS.maxSequenceDots.max)
        .optional(),
    sequenceQuestionAnswerCounts: z
        .array(z
        .number()
        .int()
        .min(COURSE_IMPORT_NESTED_BOUNDS.answerCount.min)
        .max(COURSE_IMPORT_NESTED_BOUNDS.answerCount.max))
        .min(1)
        .max(6)
        .optional(),
    composeSequenceQuestions: z.boolean().optional(),
    minItemsByLayout: z
        .record(z.string().min(1), z
        .number()
        .int()
        .min(COURSE_IMPORT_NESTED_BOUNDS.layoutMinItems.min)
        .max(COURSE_IMPORT_NESTED_BOUNDS.layoutMinItems.max))
        .optional(),
})
    .strict();
/**
 * Every rule key as a value, so a test — and the Console, and the route — can
 * iterate them.
 *
 * ⚠️ NO COUNT IN THIS SENTENCE, deliberately. It said "the seven keys" while
 * holding eleven; a number in a comment beside the list it counts is a second
 * copy of the list, and it drifts the first time somebody appends to one and not
 * the other (`feedback_a_plan_that_only_grows_stops_being_a_plan`).
 */
export const COURSE_IMPORT_RULE_KEYS = Object.freeze([
    "minScreensPerModule",
    "alternateImagePlacement",
    "openingLayouts",
    "verticalTabsMinBodyChars",
    "coverageCheck",
    "maxConsecutiveSameLayout",
    "minVideoScreensPerCourse",
    "composableFields",
    "sourceQuestionsUseQuizLayout",
    "varyLayoutsAcrossModules",
    "allowedLayouts",
    // The 2026-09-21 layout rules, in the brief's own rule order.
    "wordBands",
    "wordBandHardLimits",
    "bandWeights",
    "openingSequences",
    "maxConsecutiveByLayout",
    "maxSequenceDots",
    "sequenceQuestionAnswerCounts",
    "composeSequenceQuestions",
    "minItemsByLayout",
    "twoColumnsMinColumnChars",
    "tabTitleMaxChars",
    "balanceLayoutsWithinModule",
    "maxVideoScreensPerModule",
    "applyBandOrderToModelChoices",
]);
/** A two-level copy, so neither the frozen factory nor a caller's object leaks. */
function copyComposable(v) {
    const out = {};
    for (const [k, p] of Object.entries(v ?? {})) {
        out[k] = { title: !!p.title, subtitle: !!p.subtitle, labels: !!p.labels };
    }
    return out;
}
/**
 * A fresh, unfrozen copy of one rule's value, deep enough that nothing frozen
 * and nothing a caller owns can reach the object `resolve` hands back.
 *
 * ★ WHY EVERY KEY GOES THROUGH ONE SWITCH. The three original container rules
 * were copied by three lines at the bottom of `resolve`, which worked and did
 * not scale: eight more container-valued rules arrived at once, and a rule whose
 * copy line is forgotten is a rule the caller can mutate the FROZEN FACTORY
 * through — a defect that shows up somewhere else entirely, later, as a factory
 * value that is not the factory value. A total switch over the key union makes
 * TypeScript refuse a new key that nobody has decided about.
 */
function cloneRuleValue(key, v) {
    switch (key) {
        case "openingLayouts":
        case "allowedLayouts":
            return [...(v ?? [])];
        case "composableFields":
            return copyComposable(v);
        case "wordBands": {
            const out = {};
            for (const [id, b] of Object.entries((v ?? {}))) {
                out[id] = { p25: b.p25, p75: b.p75 };
            }
            return out;
        }
        case "wordBandHardLimits":
            return (v ?? []).map((l) => ({
                layout: l.layout,
                direction: l.direction,
                words: l.words,
            }));
        case "bandWeights":
            return (v ?? []).map((b) => ({
                maxWords: b.maxWords,
                layouts: [...b.layouts],
            }));
        case "openingSequences":
            return (v ?? []).map((seq) => [...seq]);
        case "sequenceQuestionAnswerCounts":
            return [...(v ?? [])];
        case "maxConsecutiveByLayout":
        case "minItemsByLayout":
            return { ...(v ?? {}) };
        // The scalars: a number or a boolean cannot be mutated through.
        case "minScreensPerModule":
        case "alternateImagePlacement":
        case "verticalTabsMinBodyChars":
        case "coverageCheck":
        case "maxConsecutiveSameLayout":
        case "minVideoScreensPerCourse":
        case "sourceQuestionsUseQuizLayout":
        case "varyLayoutsAcrossModules":
        case "maxSequenceDots":
        case "composeSequenceQuestions":
        case "twoColumnsMinColumnChars":
        case "tabTitleMaxChars":
        case "balanceLayoutsWithinModule":
        case "maxVideoScreensPerModule":
        case "applyBandOrderToModelChoices":
            return v;
    }
}
/**
 * The longest run of `layoutId` allowed, per-layout override first.
 *
 * ★ ONE FUNCTION, because the fallback IS the rule. `maxConsecutiveByLayout` is
 * an override map over `maxConsecutiveSameLayout` (see both fields' comments),
 * and a caller that reads either one on its own gets a wrong answer in a
 * direction that looks plausible: the map alone says a layout nobody named has
 * no limit at all, and the global alone says `text_and_image` may never repeat.
 * Neither is the rule (`feedback_one_rule_one_place`).
 */
export function maxConsecutiveFor(rules, layoutId) {
    const own = rules.maxConsecutiveByLayout[layoutId];
    return typeof own === "number" ? own : rules.maxConsecutiveSameLayout;
}
/**
 * Merge the stored overrides onto the factory values.
 *
 * ⚠️ `undefined` means "not overridden". An override that happens to EQUAL the
 * factory value is still reported as `stored`, deliberately: it was typed by a
 * person, and it stops tracking the factory the moment the factory changes.
 * Reporting it as `factory` would make the screen lie about what happens next.
 */
export function resolveCourseImportRules(stored) {
    const s = stored ?? {};
    const sources = {};
    const rules = { ...FACTORY_COURSE_IMPORT_RULES };
    for (const key of COURSE_IMPORT_RULE_KEYS) {
        const v = s[key];
        if (v === undefined || v === null) {
            sources[key] = "factory";
            continue;
        }
        sources[key] = "stored";
        rules[key] = v;
    }
    // Everything is frozen on the factory side and owned by the caller on the
    // stored side, so EVERY value is copied on the way out — one pass, after the
    // merge, rather than a copy line per container rule that someone has to
    // remember to add. Copying a value that was already copied is free.
    for (const key of COURSE_IMPORT_RULE_KEYS) {
        const store = rules;
        store[key] = cloneRuleValue(key, store[key]);
    }
    return {
        rules,
        sources,
        isEntirelyFactory: COURSE_IMPORT_RULE_KEYS.every((k) => sources[k] === "factory"),
    };
}
// ── What the model is told ──────────────────────────────────────────────────
/**
 * The block injected into the outline prompt as `{{IMPORT_RULES}}`.
 *
 * ★ THE POINT OF THIS FUNCTION: the Console's "Exactly what gets sent" tab calls
 * it too. What Omar reads on screen is therefore the same string the model
 * receives, produced by the same code — not a description of it. A description
 * is a second copy of the rule and it goes stale silently
 * (`feedback_a_check_can_match_the_prose_instead_of_the_value`).
 *
 * ⚠️ Only the rules a MODEL can act on appear here. Alternating image placement
 * and the coverage pass are performed by code — asking a model to do something
 * that is already deterministic invites it to do it differently.
 */
/** a) b) c) … — the brief's own labels for the approved module openings. */
const OPENING_SEQUENCE_LETTERS = "abcdefgh".split("");
/**
 * One ranking row rendered as a range: "0–20", "21–40", "261 and above".
 *
 * ★ THE LOWER EDGE IS DERIVED, never stored. Storing both edges lets two
 * adjacent rows disagree about where they meet — and a gap between them is a
 * word count that matches NO row, which reads downstream as "no candidate
 * layout" rather than as a broken table.
 */
function wordBandRange(bands, i) {
    const previous = i === 0 ? -1 : bands[i - 1].maxWords;
    const lower = previous + 1;
    const b = bands[i];
    return b.maxWords === WORD_BAND_NO_UPPER_LIMIT
        ? `${lower} and above`
        : `${lower}–${b.maxWords}`;
}
export function courseImportRulesPromptBlock(rules, labelFor) {
    const name = (id) => (labelFor ? labelFor(id) : id);
    const lines = [];
    let n = 0;
    lines.push("These rules are set by Dynamo staff in the Console and can change between " +
        "imports. They apply IN ADDITION to the absolute rules above, and they " +
        "never override this one:", "", "⚠️ **Never invent, duplicate or pad content to satisfy a number below.** " +
        "If the document cannot support a rule, follow the document: return what " +
        "it supports and name the rule you could not meet in `note`. A short, " +
        "honest course is the correct answer; a padded one is not.", "");
    lines.push(`${++n}. **At least ${rules.minScreensPerModule} ${rules.minScreensPerModule === 1 ? "screen" : "screens"} in every module.** Choose module boundaries so this holds — do not create a ` +
        `module the document cannot fill to that size.`);
    if (rules.openingLayouts.length) {
        lines.push(`${++n}. **The FIRST screen of every module must be one of:** ` +
            `${rules.openingLayouts.map((id) => `\`${name(id)}\``).join(", ")}.`);
    }
    if (rules.openingSequences.length) {
        // Rule 3. ⚠️ Deliberately NOT worded as "the FIRST screen of every module" —
        // that is `openingLayouts`' sentence above, and a test switches that rule off
        // and asserts its sentence is gone. Two rules that read the same cannot be
        // told apart by anything, including by the model.
        lines.push(`${++n}. **Open every module with one of these approved shapes:** ` +
            rules.openingSequences
                .map((seq, i) => `**${OPENING_SEQUENCE_LETTERS[i] ?? String(i + 1)})** ` +
                seq.map((id) => `\`${name(id)}\``).join(" → "))
                .join("; ") +
            `. Choose by what the SECOND screen's content actually is — a passage a ` +
            `person would say out loud, parallel or comparative material, or an ` +
            `explanation that moves in steps. ⚠️ If the document cannot fill an opening ` +
            `of this shape, follow the document: build the module the document ` +
            `supports and name this rule in \`note\`. Never invent a screen to complete ` +
            `a shape.` +
            // ★ THE ONE PLACE THESE TWO RULES ARE RECONCILED, and it is additive: the
            // variety rule's own sentence is not touched. Rule 3 says an opening
            // shape is MEANT to repeat in every module; the variety rule says do not
            // repeat a three-screen shape in every module. Both are true of different
            // screens, and a model sent both without this sentence is sent a
            // contradiction — the §2 ④ defect that was paid for on every import until
            // 2026-09-21. The brief's own conflict table resolves it the same way
            // ("Keep Rule 3"). Rendered only when the variety rule is actually on, so
            // it can never refer to a rule that is not in the prompt.
            (rules.varyLayoutsAcrossModules
                ? ` ⚠️ This opening shape is MEANT to repeat in every module. Where the ` +
                    `variety rule below asks you to vary the pattern between modules, that ` +
                    `applies to the screens AFTER the opening — never to the opening itself.`
                : ""));
    }
    const bandRows = Object.entries(rules.wordBands);
    if (bandRows.length) {
        // Rule 1. ⚠️ THE UNIT IS LOAD-BEARING and is repeated in the disclaimer
        // rather than only in the heading: D3 forbids converting between these
        // whole-screen WORD totals and the per-field CHARACTER budgets, and the one
        // time that conversion was made in this project it inverted a conclusion.
        // A test asserts no line carrying a figure from this table also carries the
        // word "character".
        lines.push(`${++n}. **Size the screen to the WORDS it has to carry.** The figures below ` +
            `are whole-screen word totals — every word the finished screen shows, all ` +
            `its fields added together — measured across six real courses. Prefer a ` +
            `layout whose band contains the passage's word count.`, "", "| layout | typical whole-screen words |", "|---|---|", ...bandRows.map(([id, b]) => `| \`${name(id)}\` | ${b.p25}–${b.p75} |`), "", "⚠️ These are WORD counts for a WHOLE screen. They are not per-field " +
            "character limits and one is never converted into the other. They choose a " +
            "layout; they are never a length to write to. If the document cannot " +
            "support this, follow the document — keep its text whole, take the nearest " +
            "layout that can hold it, and name this rule in `note`. Never trim a " +
            "passage to reach a band and never pad one to fill it.", "");
    }
    if (rules.wordBandHardLimits.length) {
        // Rule 1's three clean limits. ⚠️ No count in the sentence — the list is
        // rendered from the data, and "three" beside a list of four is how a prompt
        // starts disagreeing with itself.
        lines.push(`${++n}. **Whole-screen word limits that are never crossed:** ` +
            rules.wordBandHardLimits
                .map((l) => `\`${name(l.layout)}\` never ` +
                `${l.direction === "never_above" ? "above" : "below"} ${l.words} words`)
                .join("; ") +
            `. Where a passage would break one of them, give the passage a different ` +
            `layout. ⚠️ Never shorten the document's text to get under a ceiling and ` +
            `never pad it to reach a floor: if the document cannot support this, ` +
            `follow the document and say so in \`note\`.`);
    }
    if (rules.bandWeights.length) {
        // Rule 2. The lower edge of a row is the previous row's upper edge + 1, so
        // the rendered range is derived and two adjacent rows cannot disagree.
        lines.push(`${++n}. **When more than one layout fits, take them in this order.** The ` +
            `first name in a row is the first choice; move along the row only when the ` +
            `one before it cannot be used — for instance because it would repeat the ` +
            `previous screen's type.`, "", "| whole-screen words | in order of preference |", "|---|---|", ...rules.bandWeights.map((b, i) => `| ${wordBandRange(rules.bandWeights, i)} | ` +
            `${b.layouts.map((id) => `\`${name(id)}\``).join(", ")} |`), "", "⚠️ This order decides between layouts that ALREADY fit the content. It is " +
            "never a reason to reshape a passage so it lands in a different row, and " +
            "never a reason to split one idea across two screens. If the document " +
            "cannot support any layout in the row, follow the document and name this " +
            "rule in `note`.", "");
    }
    // ⚠️ THE FIRST SENTENCE BELOW IS PINNED BY A TEST IN THE GATEWAY and is
    // reproduced unchanged. Rule 4's per-layout exception is APPENDED to the same
    // numbered rule rather than added as a rule of its own, because a prompt that
    // says "never two in a row" in one numbered rule and "three are fine" in
    // another is exactly the contradiction this project already paid for on every
    // import until 2026-09-21 (§2 ④ of the design doc, `feedback_one_rule_one_place`).
    const repeatExceptions = Object.entries(rules.maxConsecutiveByLayout)
        .filter(([, limit]) => limit > rules.maxConsecutiveSameLayout)
        .map(([id, limit]) => `\`${name(id)}\` may run up to ${limit} in a row`);
    lines.push((rules.maxConsecutiveSameLayout === 1
        ? `${++n}. **Never place two screens of the same type next to each other.** ` +
            `Not across a module boundary either — a learner walking the course does ` +
            `not notice where one module ended.`
        : `${++n}. **Never use the same type more than ${rules.maxConsecutiveSameLayout} ` +
            `times in a row**, across module boundaries included.`) +
        (repeatExceptions.length
            ? ` ${repeatExceptions.length === 1 ? "**One exception, and only this:**" : "**Exceptions, and only these:**"} ` +
                `${repeatExceptions.join("; ")}. ⚠️ A permitted repeat is permission and ` +
                `never a target: if the document does not carry that much material, use ` +
                `fewer screens.`
            : ""));
    if (rules.verticalTabsMinBodyChars > 0) {
        lines.push(`${++n}. **\`vertical_tabs\` needs real body text.** Choose it only where ` +
            `EVERY tab you propose can carry at least ${rules.verticalTabsMinBodyChars} ` +
            `characters of the document's own words. A tab strip of one-line tabs is ` +
            `\`icons_discover\`, not this.`);
    }
    if (rules.minVideoScreensPerCourse > 0) {
        // ── ⚠️ THE ESCAPE HATCH CAME OUT — 2026-09-22 ──────────────────────────
        //
        // The old wording ended *"If the document has fewer such passages than
        // that, say so in `note`"*, and on run 11a08280 the model took it: its
        // whole `note` was *"The document does not support a module opening with a
        // video as required by the course import rules"*, and the course got ZERO
        // video screens against a quota of two.
        //
        // Omar, reading that: *"Never set aside the video requirement for 'text not
        // narration-ready.' Confirmed: always produce the required video screens,
        // using the best-available source passage as a draft script — an ordinary
        // explanatory passage becoming a provisional script, keeping its original
        // wording, without needing to already read like a narrated script."*
        //
        // ★ WHAT REPLACES IT IS NOT "TRY HARDER". Two things changed together, and
        //   the prompt is the smaller half: the rule engine now promotes the fullest
        //   remaining prose passage itself, so the quota no longer depends on the
        //   model agreeing to meet it. What the prompt does is stop ASKING for the
        //   refusal — and lower the bar it was refusing against, by saying in as
        //   many words that an ordinary explanation is enough
        //   (`feedback_a_model_may_decline_so_a_guarantee_must_not_ask_one`).
        //
        // ⚠️ The document still outranks the number. What is removed is the
        //    judgement about READINESS; the refusal that survives is structural —
        //    a list or a procedure spoken as one paragraph loses its parts.
        lines.push(`${++n}. **At least ${rules.minVideoScreensPerCourse} video ${rules.minVideoScreensPerCourse === 1 ? "screen" : "screens"} in the whole course** (\`fullscreen_video\` or \`small_video\`). Prefer the ` +
            `passages a person would SAY — a welcome, a scenario, one idea explained ` +
            `end to end. ⚠️ A passage does not have to already READ like a script. An ` +
            `ordinary explanatory passage becomes the draft script, in its own words, ` +
            `for the author to edit; "this is not written as narration" is not a reason ` +
            `to leave the quota unmet. The only passages to keep off a video screen are ` +
            `lists, procedures, comparisons and questions, whose parts would be lost ` +
            `inside one spoken paragraph. ⚠️ Never duplicate a passage onto a second ` +
            `screen and never write one to reach this number.`);
    }
    if (rules.sourceQuestionsUseQuizLayout) {
        lines.push(
        // ⚠️ THIS SENTENCE USED TO CONTRADICT THE PROMPT IT IS APPENDED TO, and the
        // audit of 2026-09-21 found them sitting in the same request to the model.
        // `course_outline.md` says "ALL the document's questions on ONE screen …
        // Never one screen per question"; this block said "each one is its own
        // `quiz_images` screen". The CODE consolidates each question region into
        // one screen (`consolidateSourceQuestions`), which is what the Console's
        // own plain-English description promises, so this half was both wrong and
        // paid for on every import. Keep the two in agreement
        // (`feedback_one_rule_one_place`).
        `${++n}. **A passage that IS a question becomes \`quiz_images\`.** Where the ` +
            `document already contains questions — a quiz, a knowledge check, a "test ` +
            `yourself" section — they are collected onto ONE \`quiz_images\` screen, in ` +
            `the document's order, each in its own question slot. Never one screen per ` +
            `question, and never question content in any other layout. The answers and ` +
            `feedback come from the document. This rule OUTRANKS the opening-layout ` +
            `rule and the no-two-in-a-row rule: if a module is a quiz, its screens stay ` +
            `\`quiz_images\` even if that means two in a row or a module that does not ` +
            `open on an approved layout. Say so in \`note\`.`);
    }
    if (rules.varyLayoutsAcrossModules) {
        lines.push(`${++n}. **Vary the pattern BETWEEN modules, not only inside one.** Repeating ` +
            `the same three-screen shape in every module satisfies the rule above and ` +
            `still produces a monotonous course. Reach for the whole catalogue where ` +
            `the content supports it — \`horizontal_tabs\` for parallel topics, ` +
            `\`icons_discover\` for short named concepts, \`two_columns_text\` for a ` +
            `comparison, \`title\` for a chapter beat. ⚠️ Do not manufacture variety by ` +
            `splitting one idea across three screens or by choosing a shape the source ` +
            `cannot fill: the right layout for the content always wins over variety.`);
    }
    if (rules.allowedLayouts.length) {
        lines.push(`${++n}. **Use ONLY these layout types:** ` +
            `${rules.allowedLayouts.map((id) => `\`${name(id)}\``).join(", ")}.`);
    }
    const composable = composablePromptTable(rules, name);
    if (composable)
        lines.push("", composable);
    return lines.join("\n");
}
/**
 * The editorial-freedom table, rendered for a model.
 *
 * ★ It is stated as a PERMISSION with a boundary, never as an instruction to
 * write. The distinction is the whole point of Omar's rule: *"Newly composed
 * titles and descriptions cannot substitute for missing instructional content."*
 */
export function composablePromptTable(rules, labelFor) {
    const name = (id) => (labelFor ? labelFor(id) : id);
    const rows = Object.entries(rules.composableFields)
        .filter(([, p]) => p.title || p.subtitle || p.labels)
        .map(([id, p]) => {
        const allowed = [];
        if (p.title)
            allowed.push("screen title");
        if (p.subtitle)
            allowed.push("descriptive subtitle");
        if (p.labels)
            allowed.push("tab / step / icon labels");
        return `| \`${name(id)}\` | ${allowed.join(", ")} |`;
    });
    if (!rows.length)
        return "";
    return [
        "**What you may WRITE, rather than copy.**",
        "",
        "Titles and short labels in the document's own language may be composed by you",
        "where the table allows it — they should be short, specific and grounded in the",
        "passage they sit on. Everything NOT in this table is copied from the document:",
        "bodies, tab bodies, video scripts, questions, answers, feedback and every fact.",
        "",
        "⚠️ **A composed title never substitutes for missing content.** If a screen has no",
        "source text, the answer is fewer screens, not a better headline.",
        "",
        "| layout | fields you may compose |",
        "|---|---|",
        ...rows,
    ].join("\n");
}
/**
 * The block injected into the screen-writer prompt as `{{IMPORT_RULES}}`.
 *
 * The writer only needs the rules that bear on the WORDS. Its own type table
 * already carries every length budget; what it cannot know is the tab-body
 * floor, because that floor is what decides whether the screen keeps its type
 * at all.
 */
export function courseImportWriterRulesBlock(rules) {
    const lines = [];
    if (rules.verticalTabsMinBodyChars > 0) {
        lines.push(`- **A \`vertical_tabs\` tab body must carry at least ` +
            `${rules.verticalTabsMinBodyChars} characters** of the document's own text, ` +
            `spaces included. Where the source cannot support that for a tab, return ` +
            `FEWER tabs rather than a thin one — the caller changes the screen's type ` +
            `when the bodies are too short, and a thin tab is the thing that triggers it.`);
    }
    if (rules.maxSequenceDots > 0) {
        lines.push(`- **A \`sequence\` screen carries at most ${rules.maxSequenceDots} dots**, ` +
            `counting every one of them — the content steps AND any closing question ` +
            `with its feedback. Where a passage has more steps than that, return ` +
            `FEWER, fuller dots rather than an extra one. ⚠️ Never drop the document's ` +
            `text to make it fit and never split one idea in two to reach the count: ` +
            `if the document cannot support this, follow the document.`);
    }
    // ── Rule 9, the display limit on a tab title ─────────────────────────────
    //
    // ★ TOLD TO THE MODEL, because this is the only place the title can be made
    //   short without anything being lost: the model is the thing that WRITES it,
    //   and a shortening applied afterwards can only drop words. The check on the
    //   other side (`enforceTabTitles`) exists for what this sentence does not
    //   achieve, and it refuses to cut a word that is not also in the tab's body.
    if (rules.tabTitleMaxChars > 0) {
        lines.push(`- **A tab title on \`vertical_tabs\` or \`horizontal_tabs\` is at most ` +
            `${rules.tabTitleMaxChars} characters, spaces included — THIS OVERRIDES THE ` +
            `SLOT TABLE'S LONGER FIGURE.** ⚠️ The slot table gives these titles a ` +
            `per-field storage budget; this is the DISPLAY limit, it is shorter, and it ` +
            `is the one to obey. On a real import every over-long title came back ` +
            `inside the table's budget and outside this one — the table was followed ` +
            `and this line was not.\n` +
            `  Past ${rules.tabTitleMaxChars} characters the text overflows the tab ` +
            `button. Where the document's heading is longer, write a SHORTER title that ` +
            `is faithful to that tab's content — never cut a word part-way through, and ` +
            `never use an ellipsis. **Aim for ONE or TWO short words.** A two-word title ` +
            `whose words are long cannot be rescued afterwards: dropping either word ` +
            `changes what it says, so the caller leaves it over-long and records it. ` +
            `⚠️ **The heading's own words are not lost by this:** the full heading stays ` +
            `in that tab's body text, exactly as the document wrote it. Shortening a ` +
            `title must never remove a word from the course.`);
    }
    // ── Rule 6, and the switch that turns it off ─────────────────────────────
    //
    // ★ ONE BRANCH, BOTH DIRECTIONS STATED. When the pair is off the model is
    // told so in as many words rather than simply not being told to write one:
    // the prompt's own rule 7 names `sequence` as a place a question may be
    // composed, and a silence beside that sentence is an invitation, not a
    // prohibition.
    if (rules.composeSequenceQuestions) {
        lines.push(`- **A \`sequence\` MAY end in a question you compose, and it is the ONLY ` +
            `question you ever compose anywhere.** It is two dots, never one: the ` +
            `question, then its feedback, as the LAST two dots of the screen. Write it ` +
            `from the meaning of the dots before it — a question that quotes the text ` +
            `is not a question. ⚠️ **It must never replace the document's words.** If ` +
            `the passage already fills this screen's dots with the document's own ` +
            `text, there is NO question: fewer real steps beat a question that cost a ` +
            `paragraph. And it is never composed on \`quiz_images\`, whose questions ` +
            `come from the document or do not exist.`);
    }
    else {
        lines.push(`- **Compose NO questions at all on this import.** A \`sequence\` carries ` +
            `only the document's own steps; do not add a closing question or a ` +
            `feedback dot. The document's own questions are unaffected — where the ` +
            `source carries a quiz it is still used, word for word, on \`quiz_images\`.`);
    }
    if (rules.composeSequenceQuestions && rules.sequenceQuestionAnswerCounts.length) {
        // ⚠️ D6, verbatim: *"The 2/3 alternation is not a constraint — do not encode
        // it. Instrument it."* So this says which counts are permitted and NOTHING
        // about how often each should appear. D6 permits a prompt-level nudge to
        // vary them, and the one word "Vary" below is that nudge and its whole
        // extent: no ratio, no cadence, no per-module or per-course figure, so
        // nothing in this file can be mistaken later for the encoded rule D6
        // forbids.
        const counts = rules.sequenceQuestionAnswerCounts;
        const list = counts.length === 1
            ? String(counts[0])
            : `${counts.slice(0, -1).join(", ")} or ${counts[counts.length - 1]}`;
        lines.push(`- **A question YOU compose on a \`sequence\` carries ${list} answers** — ` +
            `never more and never fewer. Exactly one is correct, and the feedback says ` +
            `why. Vary them where the passage supports it. ⚠️ Never invent an answer ` +
            `to reach a count: if the passage supports only ${counts[0]}, write ` +
            `${counts[0]}. A question the DOCUMENT supplies keeps its own shape and ` +
            `its own answers — this applies only to a question you write yourself.`);
    }
    const composable = composablePromptTable(rules);
    if (composable) {
        lines.push("- **Fill EVERY field the layout has.** A field you leave out keeps the " +
            "stand-in text the editor created it with — `Lorem ipsum…` — and that " +
            "text ships. If a field has no source to copy from, compose it within " +
            "the permissions below; if it is a body and there is no source, return " +
            "FEWER items rather than an empty one.", "", composable);
    }
    if (!lines.length) {
        lines.push("- No additional import rules are set for this import.");
    }
    return lines.join("\n");
}
/**
 * What the Console shows under "What can never be changed, and why" — the
 * guarantees that hold whatever the seven numbers say.
 *
 * These are not settings and there is no shape in which they could become
 * settings: each one names something the code does not do rather than something
 * it is configured not to do.
 */
export const COURSE_IMPORT_INVARIANTS = Object.freeze([
    Object.freeze({
        title: "Content is never invented, duplicated or padded",
        body: "Every rule above is a ceiling on ambition, never a quota. When a document " +
            "cannot support a minimum, the import reports the shortfall and stops there. " +
            "Nothing in the code manufactures a screen, a tab or a video to make a number " +
            "come out right, so no setting can make it start.",
    }),
    Object.freeze({
        title: "An import uses one set of rules from start to finish",
        body: "The rules are read once, when a run begins, and that snapshot is what the " +
            "whole run uses — the outline, every module, the coverage pass and every " +
            "check. Saving a change here while an import is running cannot change that " +
            "import.",
    }),
    Object.freeze({
        title: "Changing a rule never touches a course that already exists",
        body: "These settings shape what the NEXT import produces. No save here rewrites a " +
            "generated course, and no save here touches an author's edits.",
    }),
    Object.freeze({
        title: "No picture or video is ever generated",
        body: "A generated screen gets the existing stand-in artwork and the existing " +
            "stand-in reel, which ship exactly as they are and raise a warning rather " +
            "than blocking the build. Nothing on this screen can turn image or video " +
            "generation on.",
    }),
    // ⚠️ THIS ENTRY STATED TWO REQUIREMENTS THAT DO NOT EXIST, corrected
    // 2026-09-21 after the Course Import audit measured each claim against the
    // schema it was describing.
    //
    //   "a tab strip needs at least three tabs" — true of `horizontal_tabs` and
    //   `icons_discover` (both `.min(3)`), and FALSE of `vertical_tabs` and
    //   `sequence`, which are `.min(1)`. Measured by building one-tab screens of
    //   each and parsing them, with `horizontal_tabs` as the control that
    //   correctly rejected two and accepted three.
    //
    //   "a quiz needs four answers" — there is no such rule anywhere.
    //   `QuizAnswerSchema` and `SequenceQuestionOptionSchema` are both `.min(2)`
    //   with NO maximum, and `course_outline.md` already tells the model "never
    //   padded to four".
    //
    // ★ Both were on the ONE tab of the Console that exists to tell Omar what
    // the code guarantees whatever he sets — the worst possible place to be
    // wrong, because it is the page a person consults instead of reading the
    // code. Say only what is actually enforced, and name the layouts rather than
    // generalising across them.
    Object.freeze({
        title: "A layout's own shape still wins",
        body: "A layout's own requirements are enforced after these rules and cannot be " +
            "relaxed by them: a horizontal tab strip and an icon grid each need at " +
            "least three items, a question needs at least two answers, and a video " +
            "screen needs a script the author can read. Where a layout's minimum " +
            "differs from what these settings ask for, the layout's own minimum is " +
            "the one that holds.",
    }),
    Object.freeze({
        title: "Only Dynamo staff can change these",
        body: "The authoring runtime can read these rules and has no privilege to write " +
            "them — that is a database grant, not a missing screen. An author can never " +
            "alter how their own import is judged.",
    }),
]);
// ── Measuring a tab body, which is the one rule with a subtle definition ────
/**
 * How many characters of REAL TEXT a rich-text value carries.
 *
 * Omar: *"Count actual text, excluding formatting markup and content from other
 * fields."* So: tags removed, entities resolved, runs of whitespace counted as
 * one space, ends trimmed. `&nbsp;` counts as a space rather than as six
 * characters — a paragraph of non-breaking spaces is empty, and a threshold it
 * could satisfy would be a threshold about markup.
 *
 * ⚠️ Deliberately NOT a DOM parse: this runs in the gateway, where there is no
 * DOM, and the input is the model's own plain prose wrapped by the editor. The
 * tag strip is therefore the conservative direction — it can only ever count
 * FEWER characters than a person would, so a screen never passes the threshold
 * on markup it does not have.
 */
/** A code point, or a space where it is not one — never a throw. */
function codePointOrSpace(cp) {
    return Number.isFinite(cp) && cp >= 0 && cp <= 0x10ffff ? String.fromCodePoint(cp) : " ";
}
export function richTextOf(value) {
    if (typeof value !== "string")
        return "";
    const withoutTags = value.replace(/<[^>]*>/g, " ");
    const withoutEntities = withoutTags
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&quot;/gi, '"')
        // ⚠️ CLAMPED — `String.fromCodePoint` RAISES above U+10FFFF. The decimal form
        //    has been unclamped here since before this change and is live; the hex
        //    form was added with it. Either would throw out of `richTextLength`, the
        //    column checks and the placement pass
        //    (`feedback_bound_anything_that_gates_a_write`).
        .replace(/&#(\d+);/g, (_m, d) => codePointOrSpace(Number(d)))
        .replace(/&#[xX]([0-9a-fA-F]+);/g, (_m, h) => codePointOrSpace(parseInt(h, 16)))
        // ⚠️ THE COMMON NAMED ENTITIES BECOME THEIR CHARACTER, not a space. A
        //    retrospective review measured the cost of the blanket rule below: a tab
        //    body holding `Causes &mdash; factors` read as `Causes factors`, so the
        //    check asking *"is this heading already in the body?"* said no and the
        //    heading was written in a SECOND time. A rich-text editor emits these.
        .replace(/&(mdash|ndash|lsquo|rsquo|ldquo|rdquo|hellip|apos|shy|ensp|emsp|thinsp);/gi, (_m, n) => ({
        mdash: "—", ndash: "–", lsquo: "\u2018", rsquo: "\u2019",
        ldquo: "\u201c", rdquo: "\u201d", hellip: "…", apos: "'",
        shy: "", ensp: " ", emsp: " ", thinsp: " ",
    })[n.toLowerCase()] ?? " ")
        // Anything still unrecognised becomes a space — it is punctuation or a
        // symbol, and guessing at it would be worse than a word boundary.
        .replace(/&[a-z]+[0-9]*;/gi, " ");
    return withoutEntities.replace(/\s+/g, " ").trim();
}
/**
 * ★ THE LENGTH IS THE TEXT'S LENGTH — one stripper, not two.
 *
 * Rule 9 needs the words, not just how many characters there are, and a second
 * copy of this tag-and-entity handling would drift from the first the moment
 * either was improved (`feedback_one_rule_one_place`).
 */
export function richTextLength(value) {
    return richTextOf(value).length;
}
//# sourceMappingURL=course-import-rules.js.map